import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { engineers, type Engineer } from '@/mocks/talentPool';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const roleGroups = [
  { id: 'all', label: 'All Roles' },
  { id: 'frontend', label: 'Frontend' },
  { id: 'backend', label: 'Backend' },
  { id: 'fullstack', label: 'Full-Stack' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'devops', label: 'DevOps / SRE' },
  { id: 'data', label: 'Data / AI' },
  { id: 'qa', label: 'QA / Testing' },
];

const budgetTiers = [
  { id: 'all', label: 'Any Budget' },
  { id: 'junior', label: '$35–$55 / hr', min: 35, max: 55 },
  { id: 'mid', label: '$55–$85 / hr', min: 55, max: 85 },
  { id: 'senior', label: '$85–$120 / hr', min: 85, max: 120 },
  { id: 'lead', label: '$120+ / hr', min: 120, max: 999 },
];

const topSkills = [
  'React', 'Node.js', 'Python', 'TypeScript', 'Next.js',
  'AWS', 'Kubernetes', 'PostgreSQL', 'GraphQL', 'Docker',
  'Swift', 'Flutter', 'TensorFlow', 'PyTorch', 'CI/CD',
];

const statusConfig: Record<string, { dot: string; bg: string; text: string; border: string }> = {
  'Available Now': {
    dot: 'bg-emerald-500',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
  },
  Interviewing: {
    dot: 'bg-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
  },
  'Matched This Week': {
    dot: 'bg-gray-400',
    bg: 'bg-gray-50',
    text: 'text-gray-600',
    border: 'border-gray-200',
  },
  'Returning Soon': {
    dot: 'bg-sky-500',
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200',
  },
};

function roleMatches(groupId: string, role: string): boolean {
  if (groupId === 'all') return true;
  const lower = role.toLowerCase();
  const map: Record<string, string[]> = {
    frontend: ['react', 'frontend', 'vue', 'css', 'ui', 'angular'],
    backend: ['backend', 'node', 'python', 'java', '.net', 'go', 'api'],
    fullstack: ['full-stack', 'fullstack'],
    mobile: ['mobile', 'ios', 'android', 'swift', 'react native', 'flutter'],
    devops: ['devops', 'sre', 'cloud', 'kubernetes', 'terraform', 'docker', 'ci/cd'],
    data: ['data', 'ml', 'ai', 'machine learning', 'spark', 'snowflake'],
    qa: ['qa', 'test', 'automation', 'cypress', 'selenium'],
  };
  return map[groupId]?.some((k) => lower.includes(k)) ?? true;
}

export default function TalentFinderSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const [search, setSearch] = useState('');
  const [activeRole, setActiveRole] = useState('all');
  const [activeBudget, setActiveBudget] = useState('all');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [requestingEngineer, setRequestingEngineer] = useState<Engineer | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const clearFilters = () => {
    setSearch('');
    setActiveRole('all');
    setActiveBudget('all');
    setSelectedSkills([]);
  };

  const filtered = useMemo(() => {
    return engineers.filter((e) => {
      const matchesSearch =
        !search ||
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.role.toLowerCase().includes(search.toLowerCase()) ||
        e.skills.some((s) => s.toLowerCase().includes(search.toLowerCase()));

      const matchesRole = roleMatches(activeRole, e.role);

      const matchesStack =
        selectedSkills.length === 0 || e.skills.some((s) => selectedSkills.includes(s));

      let matchesBudget = true;
      if (activeBudget !== 'all') {
        const tier = budgetTiers.find((t) => t.id === activeBudget);
        if (tier) {
          matchesBudget = e.rateMin <= tier.max && e.rateMax >= tier.min;
        }
      }

      return matchesSearch && matchesRole && matchesStack && matchesBudget;
    });
  }, [search, activeRole, activeBudget, selectedSkills]);

  const activeFilterCount =
    (activeRole !== 'all' ? 1 : 0) +
    (activeBudget !== 'all' ? 1 : 0) +
    selectedSkills.length +
    (search ? 1 : 0);

  const openRequest = (engineer: Engineer) => {
    setRequestingEngineer(engineer);
    setShowRequestModal(true);
  };

  const closeRequest = () => {
    setShowRequestModal(false);
    setTimeout(() => setRequestingEngineer(null), 300);
  };

  return (
    <section
      id="talent-finder"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
      ref={ref}
    >
      <div className="absolute top-20 right-0 w-96 h-96 bg-amber-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
      <div className="absolute bottom-10 left-0 w-72 h-72 bg-primary-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-user-search-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Talent Finder</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Find Your Perfect
            <span className="text-primary-600"> Match</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Filter by role, technology stack, and budget to discover pre-vetted engineers ready to join your team.
          </motion.p>
        </motion.div>

        {/* Search + Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 md:mb-10"
        >
          {/* Search Bar */}
          <div className="lg:col-span-2">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Search by name, role, or skill..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-navy-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-gray-500 text-sm"></i>
                </button>
              )}
            </div>
          </div>

          {/* Results Count + Clear */}
          <div className="flex items-center justify-between lg:justify-end gap-3">
            <div className="text-sm text-gray-500">
              <span className="font-bold text-navy-900">{filtered.length}</span> match{filtered.length !== 1 ? 'es' : ''}
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-navy-900 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-close-circle-line"></i>
                Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </motion.div>

        {/* Filters Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="bg-gray-50/80 border border-gray-100 rounded-2xl p-5 md:p-6 mb-8 md:mb-10"
        >
          {/* Role Filter */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</span>
              {activeRole !== 'all' && (
                <span className="text-xs text-amber-600 font-medium">{roleGroups.find((r) => r.id === activeRole)?.label}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {roleGroups.map((role) => {
                const isActive = activeRole === role.id;
                return (
                  <button
                    key={role.id}
                    onClick={() => setActiveRole(role.id)}
                    className={`px-3.5 py-2 text-xs md:text-sm font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:text-amber-700'
                    }`}
                  >
                    {role.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Budget Filter */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Budget</span>
              {activeBudget !== 'all' && (
                <span className="text-xs text-amber-600 font-medium">{budgetTiers.find((b) => b.id === activeBudget)?.label}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {budgetTiers.map((tier) => {
                const isActive = activeBudget === tier.id;
                return (
                  <button
                    key={tier.id}
                    onClick={() => setActiveBudget(tier.id)}
                    className={`px-3.5 py-2 text-xs md:text-sm font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-navy-900 text-white border-navy-900 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-navy-700 hover:text-navy-800'
                    }`}
                  >
                    {tier.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stack Filter */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tech Stack</span>
              {selectedSkills.length > 0 && (
                <span className="text-xs text-amber-600 font-medium">{selectedSkills.length} selected</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {topSkills.map((skill) => {
                const isActive = selectedSkills.includes(skill);
                return (
                  <button
                    key={skill}
                    onClick={() => toggleSkill(skill)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {isActive && <i className="ri-check-line mr-1"></i>}
                    {skill}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((engineer, index) => (
              <motion.div
                key={engineer.id}
                layout
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: -10 }}
                transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.3) }}
                className="bg-white border border-gray-100 rounded-2xl p-5 md:p-6 hover:border-amber-200 hover:shadow-lg hover:shadow-amber-500/5 transition-all duration-300 group"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-11 h-11 md:w-12 md:h-12 rounded-xl ${engineer.avatarColor} flex items-center justify-center flex-shrink-0`}
                    >
                      <span className="text-white text-sm md:text-base font-bold">
                        {engineer.initials}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-navy-900 leading-tight">
                        {engineer.name}
                      </h3>
                      <p className="text-xs text-gray-500">{engineer.role}</p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] md:text-xs font-semibold border ${statusConfig[engineer.status].bg} ${statusConfig[engineer.status].text} ${statusConfig[engineer.status].border}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[engineer.status].dot}`}></span>
                    {engineer.status}
                  </span>
                </div>

                {/* Skills */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {engineer.skills.map((skill) => (
                    <span
                      key={skill}
                      className={`px-2 py-0.5 border rounded text-[10px] md:text-xs font-medium transition-colors ${
                        selectedSkills.includes(skill)
                          ? 'bg-primary-50 border-primary-200 text-primary-700'
                          : 'bg-gray-50 border-gray-100 text-gray-600'
                      }`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                {/* Meta */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-xs font-bold text-navy-900">{engineer.years}yr</p>
                    <p className="text-[10px] text-gray-500">Experience</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-xs font-bold text-navy-900">{engineer.seniority}</p>
                    <p className="text-[10px] text-gray-500">Level</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-xs font-bold text-navy-900">{engineer.timezoneOffset}</p>
                    <p className="text-[10px] text-gray-500">
                      {engineer.timezone.split('(')[1]?.replace(')', '')}
                    </p>
                  </div>
                </div>

                {/* Highlight */}
                {engineer.highlight && (
                  <div className="flex items-start gap-2 mb-4 bg-amber-50/60 border border-amber-100 rounded-lg p-2.5">
                    <i className="ri-star-smile-line text-amber-500 text-xs mt-0.5 flex-shrink-0"></i>
                    <p className="text-[10px] md:text-xs text-amber-700 leading-relaxed">
                      {engineer.highlight}
                    </p>
                  </div>
                )}

                {/* Footer: Rate + CTA */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-400">Rate range</p>
                    <p className="text-sm md:text-base font-bold text-navy-900">
                      ${engineer.rateMin}–${engineer.rateMax}
                      <span className="text-xs font-normal text-gray-400">/hr</span>
                    </p>
                  </div>
                  <button
                    onClick={() => openRequest(engineer)}
                    className="px-4 py-2 bg-amber-500 text-white text-xs md:text-sm font-semibold rounded-lg hover:shadow-md hover:shadow-amber-500/20 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group/btn"
                  >
                    Request
                    <i className="ri-arrow-right-line ml-1 group-hover/btn:translate-x-0.5 transition-transform inline-block"></i>
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 md:py-20"
          >
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
              <i className="ri-search-2-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-bold text-navy-900 mb-2">No engineers match your criteria</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Try adjusting your filters — we add new pre-vetted engineers every week and can source specialists on demand.
            </p>
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:shadow-md transition-all cursor-pointer"
            >
              Reset All Filters
            </button>
          </motion.div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, ...springTransition }}
          className="mt-10 md:mt-14 text-center"
        >
          <p className="text-sm text-gray-500 mb-4">
            Can not find the right fit? We source specialists on demand and can expand the pool within 72 hours.
          </p>
          <a
            href="#cta"
            className="inline-flex items-center px-8 py-3.5 bg-navy-900 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            Submit a Custom Request
            <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
          </a>
        </motion.div>
      </div>

      {/* Request Match Modal */}
      <AnimatePresence>
        {showRequestModal && requestingEngineer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={closeRequest}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20 }}
              transition={{ duration: 0.4, type: 'spring', bounce: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-center gap-4 mb-5">
                <div
                  className={`w-12 h-12 rounded-xl ${requestingEngineer.avatarColor} flex items-center justify-center flex-shrink-0`}
                >
                  <span className="text-white font-bold">{requestingEngineer.initials}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-navy-900">{requestingEngineer.name}</h3>
                  <p className="text-sm text-gray-500">{requestingEngineer.role}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {requestingEngineer.skills.map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-xs font-medium text-gray-600"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 mb-5">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-xs font-bold text-navy-900">{requestingEngineer.years}yr</p>
                  <p className="text-[10px] text-gray-500">Experience</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-xs font-bold text-navy-900">{requestingEngineer.seniority}</p>
                  <p className="text-[10px] text-gray-500">Level</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="text-xs font-bold text-navy-900">${requestingEngineer.rateMin}–${requestingEngineer.rateMax}</p>
                  <p className="text-[10px] text-gray-500">/hr</p>
                </div>
              </div>

              <form
                className="space-y-3"
                method="POST"
                action="https://readdy.ai/api/form/d89b7c04og5fkb1kt6r0"
                encType="application/x-www-form-urlencoded"
                data-readdy-form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const data = new URLSearchParams();
                  for (const [key, value] of formData.entries()) {
                    data.append(key, value as string);
                  }
                  fetch(form.action, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: data.toString(),
                  })
                    .then(() => {
                      window.gtag('event', 'form_submit', {
                        form_type: 'talent_finder_request',
                        form_location: 'talent_finder_section',
                        engineer_name: requestingEngineer.name,
                        engineer_role: requestingEngineer.role,
                      });
                      closeRequest();
                    })
                    .catch(() => {
                      closeRequest();
                    });
                }}
              >
                <input type="hidden" name="requested_engineer" value={requestingEngineer.name} />
                <input type="hidden" name="requested_role" value={requestingEngineer.role} />
                <div>
                  <label htmlFor="tf-name" className="block text-xs font-medium text-gray-700 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    id="tf-name"
                    name="name"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="tf-email" className="block text-xs font-medium text-gray-700 mb-1.5">Work Email</label>
                  <input
                    type="email"
                    id="tf-email"
                    name="email"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="tf-company" className="block text-xs font-medium text-gray-700 mb-1.5">Company</label>
                  <input
                    type="text"
                    id="tf-company"
                    name="company"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="Acme Inc."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-amber-500/20 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Request This Engineer
                  <i className="ri-arrow-right-line ml-2 inline-block"></i>
                </button>
              </form>

              <button
                onClick={closeRequest}
                className="mt-3 w-full py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}